import type { SerializedNodes } from '@craftjs/core';

import type { BlockType, CommonBlock, CommonData } from './blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from './blockEditor.types';

/**
 * Maps Craft.js resolver names back to CommonData block types.
 * Core block types are defined here. Consumers can extend the transformer
 * to handle their custom block types.
 *
 * Layout components (ListItemsSection, TwoColumnLayout) survive round-trip.
 * BlockEditorCanvas (infrastructure) is flattened - its children are extracted.
 */
const RESOLVER_TO_BLOCK_TYPE: Record<string, BlockType> = {
  // Layout blocks (these use linkedNodes for their children)
  TwoColumnLayout: 'TwoColumnLayout',
  ListItemsSection: 'ListItemsSection',
};

/**
 * Craft.js infrastructure components that should NOT appear in CommonData.
 * These are structural wrappers, not actual content blocks.
 */
const INFRASTRUCTURE_COMPONENTS = new Set(['BlockEditorRoot']);

/**
 * Props that should be filtered out when saving to CommonData.
 * These are either:
 * - Not serializable (functions, React nodes)
 * - Internal Craft.js props
 */
const PROPS_TO_FILTER = new Set([
  'icon', // ReactNode (not serializable, use iconType instead)
]);

/**
 * Check if a value is serializable (can be JSON.stringify'd).
 * Returns false for functions, symbols, and objects containing them.
 */
function isSerializable(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'function' || typeof value === 'symbol') return false;
  if (typeof value !== 'object') return true;

  // Check arrays
  if (Array.isArray(value)) {
    return value.every(isSerializable);
  }

  // Check plain objects
  for (const v of Object.values(value as Record<string, unknown>)) {
    if (!isSerializable(v)) return false;
  }
  return true;
}

/**
 * Clean props by removing internal/non-serializable values.
 * Removes undefined values and props in the filter list.
 */
function cleanProps(props: Record<string, unknown>): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(props)) {
    // Skip filtered props
    if (PROPS_TO_FILTER.has(key)) continue;

    // Skip undefined values
    if (value === undefined) continue;

    // Skip non-serializable values (functions, objects with functions, etc.)
    if (!isSerializable(value)) continue;

    cleaned[key] = value;
  }

  return cleaned;
}

/**
 * Helper to extract the resolved component name from a Craft.js node type.
 * The type can be either a string or an object with resolvedName.
 */
function getResolvedName(
  nodeType: SerializedNodes[string]['type']
): string | undefined {
  if (typeof nodeType === 'string') {
    return nodeType;
  }
  if (
    typeof nodeType === 'object' &&
    nodeType !== null &&
    'resolvedName' in nodeType
  ) {
    return nodeType.resolvedName;
  }
  return undefined;
}

/**
 * Transforms Craft.js SerializedNodes back to our CommonData format.
 * This is the reverse of transformCommonDataToCraft.
 *
 * @param craftNodes - The serialized Craft.js node tree
 * @returns CommonData ready for persistence
 */
export function transformCraftToCommonData(
  craftNodes: SerializedNodes
): CommonData {
  const rootNodeIds = Object.keys(craftNodes).filter((id) => {
    const node = craftNodes[id];
    return node && !node.parent;
  });

  if (rootNodeIds.length === 0) {
    return { version: CURRENT_SCHEMA_VERSION, blocks: [] };
  }

  /**
   * Recursively process children, flattening infrastructure canvases (BlockEditorCanvas).
   * Layout components (ListItemsSection, TwoColumnLayout) are preserved as blocks.
   */
  function processChildren(nodeIds: string[]): CommonBlock[] {
    const blocks: CommonBlock[] = [];
    for (const nodeId of nodeIds) {
      const node = craftNodes[nodeId];
      if (!node) continue;

      const nodeType = getResolvedName(node.type);

      // BlockEditorCanvas is infrastructure - flatten its children
      if (nodeType === 'BlockEditorCanvas') {
        blocks.push(...processChildren(node.nodes));
      } else {
        // Everything else (layout components, content blocks) is processed as a block
        const block = processNode(nodeId);
        if (block) {
          blocks.push(block);
        }
      }
    }
    return blocks;
  }

  /**
   * Process a Craft.js node and convert it to a CommonBlock.
   * Returns null for infrastructure nodes that shouldn't be in CommonData.
   */
  function processNode(nodeId: string): CommonBlock | null {
    const node = craftNodes[nodeId];
    if (!node) return null;

    const resolvedName = getResolvedName(node.type);

    // Skip if we can't determine the component type
    if (!resolvedName) {
      return null;
    }

    // Skip infrastructure components, EXCEPT if it's a named root (not 'ROOT')
    // A named root (e.g. 'body') means it's a region container
    if (INFRASTRUCTURE_COMPONENTS.has(resolvedName) && nodeId === 'ROOT') {
      return null;
    }

    // Handle BlockEditorRoot as a container (ListItemsSection) if it's a named root
    if (resolvedName === 'BlockEditorRoot' && nodeId !== 'ROOT') {
      // It expects one child: BlockEditorCanvas
      // We want to return a ListItemsSection block with the canvas children as its children
      const canvasId = node.nodes[0];
      if (!canvasId) return null;

      const canvasNode = craftNodes[canvasId];
      if (!canvasNode) return null;

      const childBlocks = canvasNode.nodes
        .map(processNode)
        .filter((b: CommonBlock | null): b is CommonBlock => b !== null);

      return {
        id: nodeId,
        type: 'ListItemsSection',
        props: {}, // Root usually has no props we care about here
        children: { default: childBlocks },
      };
    }

    // BlockEditorCanvas is infrastructure - skip it, its children are handled by the caller.
    // When encountered via processNode (not at ROOT level), return null.
    // The parent will handle extracting children via processCanvasChildren.
    if (resolvedName === 'BlockEditorCanvas') {
      return null;
    }

    // Map to CommonData block type - if not in our map, use the resolved name directly
    // This allows for extensibility where consumers can define their own block types
    const blockType = RESOLVER_TO_BLOCK_TYPE[resolvedName] ?? resolvedName;

    // Start building the CommonBlock
    const block: CommonBlock = {
      id: nodeId,
      type: blockType,
      props: cleanProps(node.props),
    };

    // Process direct children (node.nodes)
    // These become children.default in CommonData
    if (node.nodes.length > 0) {
      const childBlocks = node.nodes
        .map(processNode)
        .filter((b: CommonBlock | null): b is CommonBlock => b !== null);

      if (childBlocks.length > 0) {
        block.children = { default: childBlocks };
      }
    }

    // Process linked nodes (named slots like leftColumn, rightColumn)
    // The linked node is typically a BlockEditorCanvas whose children
    // become children.{slotName} in CommonData
    if (Object.keys(node.linkedNodes).length > 0) {
      block.children = block.children || {};

      for (const [slotName, linkedNodeId] of Object.entries(node.linkedNodes)) {
        const linkedNode = craftNodes[linkedNodeId];
        if (!linkedNode) continue;

        // Extract children from the canvas container, flattening infrastructure canvases
        const slotChildren = processChildren(linkedNode.nodes);

        if (slotChildren.length > 0) {
          block.children[slotName] = slotChildren;
        }
      }

      // Clean up empty children object
      if (Object.keys(block.children).length === 0) {
        delete block.children;
      }
    }

    return block;
  }

  // Single ROOT node: process its children
  if (rootNodeIds.length === 1 && rootNodeIds[0] === 'ROOT') {
    const defaultRoot = craftNodes['ROOT'];
    if (!defaultRoot) {
      return { version: CURRENT_SCHEMA_VERSION, blocks: [] };
    }

    const hasDirectChildren = defaultRoot.nodes.length > 0;
    const hasLinkedChildren = Object.keys(defaultRoot.linkedNodes).length > 0;

    if (!hasDirectChildren && !hasLinkedChildren) {
      return { version: CURRENT_SCHEMA_VERSION, blocks: [] };
    }

    // If ROOT uses linkedNodes (useLinkedNodesForRootChildren: true),
    // process each linkedNode as a top-level block
    if (hasLinkedChildren) {
      const blocks: CommonBlock[] = [];

      for (const linkedNodeId of Object.values(defaultRoot.linkedNodes)) {
        const block = processNode(linkedNodeId);
        if (block) {
          blocks.push(block);
        }
      }

      return {
        version: CURRENT_SCHEMA_VERSION,
        blocks,
      };
    }

    // Otherwise, process direct children, flattening infrastructure canvases
    const blocks = processChildren(defaultRoot.nodes);

    return {
      version: CURRENT_SCHEMA_VERSION,
      blocks,
    };
  }

  // Otherwise, handle multiple roots or named roots
  const blocks = rootNodeIds
    .map((rootId) => processNode(rootId))
    .filter((b: CommonBlock | null): b is CommonBlock => b !== null);

  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks,
  };
}
